import { SPORT_LABELS, type Sport, type Rank } from '@/types';
import { SportBadge } from './SportBadge';
import { SkillLevelBadge, type SkillLevel } from './SkillLevelBadge';
import { RankBadge } from './RankBadge';
import { FeeDisplay } from './FeeDisplay';
import { Calendar, Clock, MapPin, Users, Banknote, Trophy, Handshake, Loader2, AlertTriangle, MapPinOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useMyRankProfile } from '@/hooks/useMyRankProfile';
import { useRequestJoinMatch, useMyMatchJoinRequest } from '@/hooks/useMatchJoinRequests';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { calculateRemainingSlots } from '@/lib/match-utils';
import { getRankedJoinEligibility } from '@/lib/match-eligibility';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface NearbyMatchCardProps {
  match: {
    id: string;
    sport: string;
    match_type?: '1v1' | 'team';
    status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
    date: string;
    time: string;
    location: string;
    participants?: Array<{
      id: string;
      profile_id?: string;
      profile?: {
        id?: string;
        name?: string;
        avatar?: string | null;
        rank?: Rank;
        skill_level?: string;
      };
    }>;
    created_by_profile?: {
      skill_level?: string;
      name?: string;
      avatar?: string | null;
      rank?: Rank;
    } | null;
    created_by?: string | null;
    fee?: number;
    recruiting_fee?: number;
    spectator_fee?: number;
    allow_spectators?: boolean;
    is_ranked?: boolean;
    recruiting_players?: boolean;
    recruiting_slots?: number;
    max_participants?: number | null;
    match_format?: string;
    distance?: number;
    opponent_message?: string | null;
    hasCoordinates?: boolean;
  };
  isUrgent?: boolean;
  className?: string;
}

// Helper to parse match datetime with proper timezone handling
function parseMatchDateTime(date: string, time: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes, seconds = 0] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds);
}

export function NearbyMatchCard({ match, isUrgent, className }: NearbyMatchCardProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { skillLevel: myFreshSkillLevel, rankedSport: myFreshRankedSport, isFetched: rankProfileFetched } = useMyRankProfile();
  const requestJoinMatch = useRequestJoinMatch();
  const { data: myRequest } = useMyMatchJoinRequest(match.id);

  const participants = match.participants || [];
  // Trust DB recruiting_slots — format presets already write the right value.
  const recruitingSlots = match.recruiting_slots || 0;
  const matchType = (match as any).match_type || (match as any).type;
  const creatorParticipant = participants.find(
    (p: any) => (p.profile_id || p.profile?.id) === match.created_by,
  );
  const creatorTeam = (creatorParticipant as any)?.team ?? 1;

  // Use unified slot calculation (handles profile_id and profile.id)
  const remainingRecruitingSlots = calculateRemainingSlots(
    recruitingSlots,
    participants,
    match.created_by,
    matchType,
    creatorTeam,
  );
  const joined = participants.length
    ? (participants.some((p: any) => (p.profile_id || p.profile?.id) === match.created_by)
        ? participants.length
        : participants.length + (match.created_by ? 1 : 0))
    : (match.created_by ? 1 : 0);
  const maxPlayers = (match.max_participants && match.max_participants > 0)
    ? match.max_participants
    : (recruitingSlots > 0 ? joined + remainingRecruitingSlots : joined);
  const remainingSlots = Math.max(0, maxPlayers - joined);
  
  const isCreator = profile?.id === match.created_by;
  const isParticipant = participants.some(p => (p.profile_id || p.profile?.id) === profile?.id);
  const hasPendingRequest = myRequest?.status === 'pending';
  const eligibility = getRankedJoinEligibility({
    isRanked: !!match.is_ranked,
    matchSport: match.sport,
    mySkillLevel: myFreshSkillLevel ?? (profile as any)?.skill_level,
    myRankedSport: myFreshRankedSport ?? (profile as any)?.ranked_sport,
    creatorSkillLevel: match.created_by_profile?.skill_level,
  });
  const effectiveEligible = rankProfileFetched ? eligibility.eligible : true;
  const canJoin = !!profile && !isCreator && !isParticipant && !hasPendingRequest && remainingSlots > 0 && effectiveEligible;

  // Calculate time until match with proper timezone
  const matchDateTime = parseMatchDateTime(match.date, match.time);
  const now = new Date();
  const minutesUntil = Math.floor((matchDateTime.getTime() - now.getTime()) / (1000 * 60));
  const hoursUntil = Math.floor(minutesUntil / 60);
  const isToday = new Date(match.date).toDateString() === now.toDateString();

  const getTimeDisplay = () => {
    if (minutesUntil < 60) {
      return `${minutesUntil} phút nữa`;
    } else if (hoursUntil < 24) {
      return `${hoursUntil} giờ nữa`;
    } else {
      return new Date(match.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    }
  };

  const handleQuickJoin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profile) {
      toast.error('Vui lòng đăng nhập để tham gia');
      navigate('/auth');
      return;
    }
    requestJoinMatch.mutate({ matchId: match.id });
  };


  return (
    <div
      onClick={() => navigate(`/matches/${match.id}`)}
      className={cn(
        'relative p-4 rounded-xl cursor-pointer transition-all duration-300',
        'bg-gradient-to-br from-card to-card/80 border border-border/50',
        'hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/50',
        'active:scale-[0.98]',
        isUrgent && 'border-destructive/50 shadow-destructive/10 shadow-lg',
        className
      )}
    >
      {/* Urgent badge */}
      {isUrgent && (
        <div className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
          <AlertTriangle className="w-3 h-3" />
          GẤP
        </div>
      )}

      {/* Header: Sport + Type + Distance */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <SportBadge sport={match.sport as Sport} size="md" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">
              {SPORT_LABELS[match.sport as Sport]}
            </span>
            <span className="text-xs text-muted-foreground">
              {match.match_type === 'team' ? 'Đội' : '1v1'} • {match.is_ranked ? 'Xếp hạng' : 'Giao lưu'}
            </span>
          </div>
        </div>
        
        {/* Distance or No-coordinates indicator */}
        {match.distance !== undefined ? (
          <div className="flex items-center gap-1 text-primary font-semibold text-sm bg-primary/10 px-2 py-1 rounded-full">
            <MapPin className="w-3.5 h-3.5" />
            {match.distance.toFixed(1)}km
          </div>
        ) : match.hasCoordinates === false ? (
          <div className="flex items-center gap-1 text-muted-foreground text-xs bg-muted px-2 py-1 rounded-full">
            <MapPinOff className="w-3 h-3" />
            Không có GPS
          </div>
        ) : null}
      </div>

      {/* Time & Date - Prominent */}
      <div className="flex items-center gap-3 mb-3">
        <div className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium',
          isToday ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
        )}>
          <Clock className="w-4 h-4" />
          <span>{match.time.slice(0, 5)}</span>
        </div>
        <span className={cn(
          'text-sm font-medium',
          minutesUntil < 60 ? 'text-destructive' : 'text-foreground'
        )}>
          {getTimeDisplay()}
        </span>
      </div>

      {/* Location */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
        <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <span className="truncate">{match.location}</span>
      </div>

      {/* Slots info */}
      <div className={cn(
        'flex items-center gap-1 font-medium text-sm mb-2',
        remainingSlots <= 2 ? 'text-destructive' : 'text-primary'
      )}>
        <Users className="w-4 h-4" />
        <span>{joined}/{maxPlayers} người · còn {remainingSlots} slot</span>
      </div>

      {/* Fee Section */}
      <FeeDisplay
        participationFee={match.fee}
        recruitingFee={match.recruiting_fee}
        spectatorFee={match.spectator_fee}
        matchType={match.match_type}
        isRecruiting={match.recruiting_players}
        allowSpectators={match.allow_spectators}
        className="mb-3"
      />

      {/* Skill level & Creator info */}
      {match.created_by_profile && (
        <div className="flex items-center gap-2 mb-3">
          <img
            src={match.created_by_profile.avatar || '/placeholder.svg'}
            alt={match.created_by_profile.name || 'Host'}
            className="w-6 h-6 rounded-full border border-border"
          />
          <span className="text-xs text-muted-foreground">
            {match.created_by_profile.name}
          </span>
          {match.created_by_profile.skill_level && (
            <SkillLevelBadge 
              level={match.created_by_profile.skill_level as SkillLevel} 
              size="sm" 
            />
          )}
        </div>
      )}

      {/* Opponent Message */}
      {match.opponent_message && (
        <div className="mb-3 p-2 bg-primary/5 rounded-lg border border-primary/10">
          <p className="text-xs text-foreground italic line-clamp-2">
            💬 "{match.opponent_message}"
          </p>
        </div>
      )}

      {/* Action Button */}
      {(rankProfileFetched && !eligibility.eligible && !isCreator && !isParticipant && !hasPendingRequest) ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="w-full font-semibold bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/15"
                disabled
                onClick={(e) => e.stopPropagation()}
                data-testid="nearby-match-ineligible-button"
              >
                🔒 {eligibility.shortLabel}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-xs">
              {eligibility.message}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          size="sm"
          className={cn(
            'w-full font-semibold',
            canJoin
              ? 'bg-gradient-to-r from-primary to-accent hover:opacity-90'
              : 'bg-muted text-muted-foreground'
          )}
          disabled={!canJoin || requestJoinMatch.isPending}
          onClick={handleQuickJoin}
        >
          {requestJoinMatch.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : hasPendingRequest ? (
            'Đã gửi yêu cầu'
          ) : isParticipant ? (
            'Đã tham gia'
          ) : isCreator ? (
            'Trận của bạn'
          ) : remainingSlots === 0 ? (
            'Đã đủ người'
          ) : (
            '🔥 Tham gia ngay'
          )}
        </Button>
      )}
    </div>
  );
}
